import { useEffect } from 'react';

import { ApiService } from '@/services/api-v2';

type RecordWorkspaceVisitOptions = {
  workspaceId: string | undefined;
  /** False until the session is settled enough to send an authenticated request. */
  isReady: boolean;
};

/**
 * Remembers where the user works, so a later login lands them here rather than
 * on whichever workspace happens to rank first.
 *
 * React's dependency comparison is the change detection — one request per
 * actual workspace change, and none at all while the user stays put.
 *
 * `isReady` is required, not defensive: `org` rehydrates from localStorage
 * before auth settles, and the request interceptor only attaches a token once
 * the user is loaded, so keying on the workspace id alone would fire
 * unauthenticated and 401.
 */
export function useRecordWorkspaceVisit({ workspaceId, isReady }: RecordWorkspaceVisitOptions) {
  useEffect(() => {
    if (!isReady || !workspaceId) {
      return;
    }

    void ApiService.RecordWorkspaceVisit().catch(() => {
      // Bookkeeping: failing costs the user nothing this session, and the next
      // workspace change tries again.
    });
  }, [isReady, workspaceId]);
}
